import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGrid,
    StandardModalFormGroup,
    StandardModalHelperText,
    StandardModalInfoCard,
    StandardModalInput,
    StandardModalLabel,
    StandardModalSection,
    StandardModalSectionTitle,
} from "src/components";
import type { CalendarConnection, ThemeMode } from "../types";

const CalendarChoice = styled.label`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 0.75rem;
    padding: 0.8rem 0;
    border-bottom: 1px solid var(--calendar-border);
    cursor: pointer;
`;

const CalendarChoiceText = styled.span`
    min-width: 0;
    color: var(--calendar-text);
    font-size: 0.875rem;
    font-weight: 700;
`;

type ExternalCalendarConnectionModalProps = {
    busy: boolean;
    connection: CalendarConnection | null;
    onClose: () => void;
    onConnect: (connection: CalendarConnection) => void;
    onConnectApple: (
        connection: CalendarConnection,
        email: string,
        appSpecificPassword: string
    ) => void;
    onDelete: (connection: CalendarConnection) => void;
    onSaveSelection: (connection: CalendarConnection, calendarUuids: string[]) => void;
    onSync: (connection: CalendarConnection) => void;
    open: boolean;
    themeMode: ThemeMode;
};

export function ExternalCalendarConnectionModal({
    busy,
    connection,
    onClose,
    onConnect,
    onConnectApple,
    onDelete,
    onSaveSelection,
    onSync,
    open,
    themeMode,
}: ExternalCalendarConnectionModalProps) {
    const [selectedCalendarUuids, setSelectedCalendarUuids] = useState<string[]>([]);
    const [appleEmail, setAppleEmail] = useState("");
    const [appleAppSpecificPassword, setAppleAppSpecificPassword] = useState("");

    useEffect(() => {
        setSelectedCalendarUuids(
            connection?.calendars.filter(calendar => calendar.selected).map(calendar => calendar.calendarUuid) || []
        );
        setAppleEmail("");
        setAppleAppSpecificPassword("");
    }, [connection]);

    if (!connection) {
        return null;
    }

    const toggleCalendar = (calendarUuid: string): void => {
        setSelectedCalendarUuids(previous => previous.includes(calendarUuid)
            ? previous.filter(uuid => uuid !== calendarUuid)
            : [...previous, calendarUuid]);
    };

    const appleConnection = connection.source === "apple";
    const footer = connection.connected ? (
        <>
            <StandardModalActionButton
                $variant="danger"
                disabled={busy}
                onClick={() => onDelete(connection)}
            >
                Disconnect
            </StandardModalActionButton>
            <StandardModalActionGroup style={{ marginLeft: "auto" }}>
                <StandardModalActionButton disabled={busy} onClick={() => onSync(connection)}>
                    Sync now
                </StandardModalActionButton>
                <StandardModalActionButton
                    $variant="primary"
                    disabled={busy}
                    onClick={() => onSaveSelection(connection, selectedCalendarUuids)}
                >
                    Save calendars
                </StandardModalActionButton>
            </StandardModalActionGroup>
        </>
    ) : (
        <StandardModalActionGroup style={{ marginLeft: "auto" }}>
            <StandardModalActionButton disabled={busy} onClick={onClose}>Cancel</StandardModalActionButton>
            <StandardModalActionButton
                $variant="primary"
                disabled={busy || (appleConnection && (!appleEmail.trim() || !appleAppSpecificPassword))}
                onClick={() => appleConnection
                    ? onConnectApple(connection, appleEmail.trim(), appleAppSpecificPassword)
                    : onConnect(connection)
                }
            >
                Connect
            </StandardModalActionButton>
        </StandardModalActionGroup>
    );

    return (
        <StandardModal
            description={connection.connected
                ? connection.accountEmail || "Manage this connected account."
                : "Connect an account to show its events as read-only items in Me Business."
            }
            footer={footer}
            isDarkMode={themeMode === "dark"}
            onClose={onClose}
            open={open}
            title={connection.label}
            widthMode="medium"
        >
            {connection.connected ? (
                <StandardModalSection>
                    <StandardModalSectionTitle>Calendars shown in Me Business</StandardModalSectionTitle>
                    {connection.calendars.map(calendar => (
                        <CalendarChoice key={calendar.calendarUuid}>
                            <input
                                checked={selectedCalendarUuids.includes(calendar.calendarUuid)}
                                onChange={() => toggleCalendar(calendar.calendarUuid)}
                                type="checkbox"
                            />
                            <CalendarChoiceText>{calendar.label}</CalendarChoiceText>
                        </CalendarChoice>
                    ))}
                    {connection.calendars.length === 0 && (
                        <StandardModalDescription>No calendars were returned by this account.</StandardModalDescription>
                    )}
                </StandardModalSection>
            ) : (
                <StandardModalSection>
                    <StandardModalInfoCard>
                        {appleConnection
                            ? "Apple requires an app-specific password. Your credentials are sent securely to Me Business and encrypted before storage."
                            : "You will be redirected to the provider to approve read-only calendar access."
                        }
                    </StandardModalInfoCard>
                    {appleConnection && (
                        <StandardModalFormGrid>
                            <StandardModalFormGroup>
                                <StandardModalLabel htmlFor="apple-calendar-email">
                                    Apple Account email
                                </StandardModalLabel>
                                <StandardModalInput
                                    autoComplete="username"
                                    disabled={busy}
                                    id="apple-calendar-email"
                                    onChange={event => setAppleEmail(event.target.value)}
                                    placeholder="name@example.com"
                                    type="email"
                                    value={appleEmail}
                                />
                            </StandardModalFormGroup>
                            <StandardModalFormGroup>
                                <StandardModalLabel htmlFor="apple-calendar-password">
                                    App-specific password
                                </StandardModalLabel>
                                <StandardModalInput
                                    autoComplete="new-password"
                                    disabled={busy}
                                    id="apple-calendar-password"
                                    onChange={event => setAppleAppSpecificPassword(event.target.value)}
                                    placeholder="xxxx-xxxx-xxxx-xxxx"
                                    type="password"
                                    value={appleAppSpecificPassword}
                                />
                            </StandardModalFormGroup>
                            <StandardModalHelperText style={{ gridColumn: "1 / -1" }}>
                                Generate this password under Sign-In and Security at{" "}
                                <a href="https://account.apple.com" rel="noreferrer" target="_blank">
                                    account.apple.com
                                </a>. Do not enter your normal Apple Account password.
                            </StandardModalHelperText>
                        </StandardModalFormGrid>
                    )}
                </StandardModalSection>
            )}
            {connection.lastError && (
                <StandardModalSection>
                    <StandardModalDescription color="#D64545">{connection.lastError}</StandardModalDescription>
                </StandardModalSection>
            )}
        </StandardModal>
    );
}
